/* eslint-disable react/no-unescaped-entities */
/* eslint-disable react/no-unknown-property */
import { Accordion } from 'flowbite-react';
import { motion } from 'framer-motion';

export default function FaqSection() {
  return (
    <section className="relative py-20 bg-[#FFF9F5] overflow-hidden">
      {/* Background Decorative Blurs */}
      <div className="absolute top-1/4 -right-20 w-80 h-80 bg-orange-200/30 rounded-full blur-[100px]" />
      <div className="absolute bottom-1/4 -left-20 w-80 h-80 bg-orange-100/50 rounded-full blur-[100px]" />

      <div className="max-w-4xl mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-orange-600 font-bold tracking-[0.3em] uppercase text-xs">Support Center</span>
            <h2 className="mt-3 text-4xl md:text-5xl font-black text-gray-900 uppercase">
              Common <span className="text-orange-500">Questions</span>
            </h2>
            <div className="h-1.5 w-20 bg-orange-500 mx-auto mt-6 rounded-full"></div>
          </motion.div>
        </div>

        {/* Custom Styled Accordion */}
        <div className="space-y-4">
          <Accordion flush={true} arrowIcon={() => <span className="text-orange-500 font-bold text-xl">+</span>}>
            
            {/* Question 1 */}
            <Accordion.Panel>
              <Accordion.Title className="bg-white/40 backdrop-blur-md border border-white/60 hover:bg-orange-50 transition-all rounded-2xl px-6 py-5 focus:ring-0">
                <span className="text-gray-900 font-black uppercase text-sm md:text-base tracking-tight">
                  What is "Mahakal Social Welfare Society"?
                </span>
              </Accordion.Title>
              <Accordion.Content className="px-6 py-6 bg-white/20 backdrop-blur-sm rounded-b-2xl border-x border-b border-white/40">
                <div className="prose prose-orange max-w-none text-gray-600">
                  <p className="font-bold text-orange-600 mb-2">Empowering Society Through Action.</p>
                  <p className="text-sm leading-relaxed mb-4">
                    Mahakal Social Welfare Society is a dedicated non-profit organization bridging gaps in essential services for vulnerable populations. We focus on four core pillars:
                  </p>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                    {["Direct Assistance", "Advocacy & Awareness", "Community Growth", "Capacity Building"].map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-xs font-bold text-gray-700 bg-white/50 p-2 rounded-lg">
                        <span className="w-1.5 h-1.5 bg-orange-500 rounded-full" /> {item}
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs italic text-gray-500 border-l-2 border-orange-300 pl-4">
                    Our mission is to create a more just and equitable society where every individual has the opportunity to thrive.
                  </p>
                </div>
              </Accordion.Content>
            </Accordion.Panel>

            {/* Question 2 */}
            <Accordion.Panel>
              <Accordion.Title className="bg-white/40 backdrop-blur-md border border-white/60 hover:bg-orange-50 transition-all rounded-2xl px-6 py-5 focus:ring-0">
                <span className="text-gray-900 font-black uppercase text-sm md:text-base tracking-tight">
                  Why Choose Our Society?
                </span>
              </Accordion.Title>
              <Accordion.Content className="px-6 py-6 bg-white/20 backdrop-blur-sm rounded-b-2xl border-x border-b border-white/40">
                <div className="space-y-4">
                  {[
                    { t: "Expertise", d: "Qualified professionals and trained volunteers." },
                    { t: "Holistic Approach", d: "We focus on root causes, not just immediate relief." },
                    { t: "Safe Environment", d: "Prioritizing strict confidentiality and respect." }
                  ].map((item, i) => (
                    <div key={i} className="group">
                      <h4 className="text-orange-600 font-black text-xs uppercase mb-1">{item.t}</h4>
                      <p className="text-gray-600 text-sm leading-snug">{item.d}</p>
                    </div>
                  ))}
                </div>
              </Accordion.Content>
            </Accordion.Panel>

            {/* Question 3 */}
            <Accordion.Panel>
              <Accordion.Title className="bg-white/40 backdrop-blur-md border border-white/60 hover:bg-orange-50 transition-all rounded-2xl px-6 py-5 focus:ring-0">
                <span className="text-gray-900 font-black uppercase text-sm md:text-base tracking-tight">
                  How to Contact Us for Support?
                </span>
              </Accordion.Title>
              <Accordion.Content className="px-6 py-6 bg-white/20 backdrop-blur-sm rounded-b-2xl border-x border-b border-white/40">
                <div className="text-center p-6 bg-orange-600 rounded-[2rem] shadow-xl">
                  <p className="text-white font-bold mb-4">Need immediate assistance?</p>
                  <a 
                    href="/Contact" 
                    className="inline-block bg-white text-orange-600 px-8 py-3 rounded-xl font-black text-sm uppercase transition-transform hover:scale-105 active:scale-95 shadow-lg"
                  >
                    Open Contact Form
                  </a>
                  <p className="text-orange-100 text-[10px] mt-4 uppercase tracking-widest">Our team typically responds within 24-48 hours.</p>
                </div>
              </Accordion.Content>
            </Accordion.Panel>

            {/* Question 4 */}
            <Accordion.Panel>
              <Accordion.Title className="bg-white/40 backdrop-blur-md border border-white/60 hover:bg-orange-50 transition-all rounded-2xl px-6 py-5 focus:ring-0">
                <span className="text-gray-900 font-black uppercase text-sm md:text-base tracking-tight">
                  How Can We Help You Specifically?
                </span>
              </Accordion.Title>
              <Accordion.Content className="px-6 py-6 bg-white/20 backdrop-blur-sm rounded-b-2xl border-x border-b border-white/40">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="p-4 bg-white/60 rounded-2xl">
                    <h4 className="text-orange-600 font-black text-xs uppercase mb-3 border-b border-orange-100 pb-2">Direct Assistance</h4>
                    <p className="text-gray-500 text-xs leading-relaxed">From basic needs like food and shelter to healthcare access and education scholarships.</p>
                  </div>
                  <div className="p-4 bg-white/60 rounded-2xl">
                    <h4 className="text-orange-600 font-black text-xs uppercase mb-3 border-b border-orange-100 pb-2">Capacity Building</h4>
                    <p className="text-gray-500 text-xs leading-relaxed">Providing skills training for employment and counseling for mental resilience.</p>
                  </div>
                </div>
              </Accordion.Content>
            </Accordion.Panel>

          </Accordion>
        </div>

        {/* Footer Note */}
        <p className="mt-12 text-center text-gray-400 text-xs font-medium uppercase tracking-[0.2em]">
          Still have questions? Reach out to our 24/7 help desk.
        </p>
      </div>
    </section>
  );
}